// Captures UTM params into the `mb_utm_data` cookie the server signup hook reads.

import { UTM_COOKIE_MAX_AGE_SECONDS, UTM_COOKIE_NAME, UTM_KEYS } from "@/constants/utm";
import type { UtmData } from "@/types/utm";
import { parseUtmCookieString } from "@/utils/utm-cookie";

/** Upsert current-URL UTM params into `mb_utm_data`. First-touch fields
 *  are written once, last-touch fields overwrite on every UTM-carrying hit. */
export function captureUtmFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const urlUtmParams = new Map<(typeof UTM_KEYS)[number], string>();
  for (const key of UTM_KEYS) {
    const paramValue = params.get(key);
    if (paramValue) urlUtmParams.set(key, paramValue);
  }
  const hasUtm = urlUtmParams.size > 0;
  const existing = parseUtmCookieString(document.cookie) ?? {};

  const nextCookie: UtmData = { ...existing };
  if (!existing.first_seen_at) nextCookie.first_seen_at = new Date().toISOString();
  if (hasUtm) {
    const referrer = document.referrer;
    if (!existing.first_referrer_url && referrer) nextCookie.first_referrer_url = referrer;
    for (const key of UTM_KEYS) {
      const firstKey = `first_${key}` as const;
      const lastKey = `last_${key}` as const;
      if (!existing[firstKey] && urlUtmParams.has(key)) nextCookie[firstKey] = urlUtmParams.get(key);
      if (urlUtmParams.has(key)) nextCookie[lastKey] = urlUtmParams.get(key);
    }
  }
  writeUtmCookie(nextCookie);
}

/** Serialize and write the UTM cookie with 90-day TTL, SameSite=Lax, Secure on https. */
function writeUtmCookie(data: UtmData): void {
  const value = encodeURIComponent(JSON.stringify(data));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${UTM_COOKIE_NAME}=${value}; Path=/; Max-Age=${UTM_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}
